import * as React from "react";
import { HTMLAttributes, ReactElement, useMemo, useState } from "react";
import styled from "styled-components";
import { useSpring, animated } from "react-spring";
import { toVWDesktop, toVWMobile } from "../../../helpers/styles";
import { BREAKPOINTS } from "../../../helpers/theme";

export enum ArrowButtonType {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

export type ArrowButtonProps = {
  onClick?: () => void;
  disabled?: boolean;
  type?: ArrowButtonType;
  className?: string;
} & HTMLAttributes<HTMLButtonElement>;

export const ArrowButton = ({
  onClick,
  disabled = false,
  type = ArrowButtonType.LEFT,
  className,
  ...props
}: ArrowButtonProps): ReactElement<HTMLButtonElement> => {
  const [active, setActive] = useState<boolean>(false);

  const borderAnimation = useMemo(() => {
    return {
      transform: `scale(${active ? 0.85 : 1})`,
    };
  }, [active]);
  const borderStyle = useSpring(borderAnimation);

  const arrowAnimation = useMemo(() => {
    return {
      transform: `scaleX(${active ? 1.25 : 1}) rotate(${
        type === ArrowButtonType.LEFT ? "180deg" : "0deg"
      })`,
    };
  }, [active, type]);
  const arrowStyle = useSpring(arrowAnimation);

  return (
    <Container
      onClick={onClick}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onTouchStart={() => setActive(true)}
      onTouchEnd={() => setActive(false)}
      className={className}
      disabled={disabled}
      backgroundColor={
        active ? "var(--color-green-30)" : "var(--color-green-100)"
      }
      {...props}
    >
      <Background style={borderStyle} aria-hideen={true} />
      <ArrowGreen
        xmlns="http://www.w3.org/2000/svg"
        width="20.005"
        height="10.021"
        viewBox="0 0 20.005 10.021"
        style={arrowStyle}
      >
        <g
          data-name="Icon / Arrow"
          transform="translate(20.005 10.021) rotate(180)"
        >
          <path
            data-name="Path 3555"
            d="M20.75,10.75h-13V7.744A.754.754,0,0,0,6.48,7.2L2.42,11.016a1.351,1.351,0,0,0,0,1.968L6.48,16.8a.753.753,0,0,0,1.27-.548V13.25h13a1.25,1.25,0,0,0,0-2.5Z"
            transform="translate(-1.995 -6.989)"
            fill="var(--color-green-30)"
          />
        </g>
      </ArrowGreen>
    </Container>
  );
};

const Container = styled.button<{ backgroundColor: string; disabled: boolean }>`
  width: ${toVWMobile(46)}vw;
  height: ${toVWMobile(46)}vw;
  border: none;
  background-color: ${(props) => props.backgroundColor};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  ${({ disabled }) =>
    disabled &&
    `
    img{
      opacity: 0.5;
    }
  `}
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toVWDesktop(46)}vw;
    height: ${toVWDesktop(46)}vw;
  }
`;

const Background = styled(animated.div)`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: var(--color-green-100);
  position: absolute;
`;

const ArrowGreen = styled(animated.svg)`
  width: ${toVWMobile(20)}vw;
  height: auto;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toVWDesktop(20)}vw;
    height: auto;
  }
`;

export default ArrowButton;
